import React, { useState } from 'react';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import { Box, IconButton, Menu, MenuItem, styled } from '@mui/material';
import { CommonEntityState } from '../../../data/models/entity-state';
import CommonEntityStateChip from '../shared/CommonEntityStateChip';
import { MoreVert } from '@mui/icons-material';
import TransportationAttributesGrid from './TransportationAttributesGrid';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

export interface TransportationAttributeRowItem {
  transportationAttributeUUID: string;
  name: string;
  category: string;
  status: CommonEntityState;
  children?: TransportationAttributeRowItem[];
}

export interface TransportationAttributeGridRow {
  id: string;
  transportationAttributeUUID: string;
  name: string;
  category: string;
  status: CommonEntityState;
  hierarchy: string[];
}

interface TransportationAttributesGridContainerProps {
  rowItems: TransportationAttributeRowItem[];
  isLoading?: boolean;
  handleMenuItemClick: (
    menuItem: TransportationAttributeMenuItem,
    transportationAttributeUUID: string,
  ) => void;
}

export enum TransportationAttributeMenuItem {
  Edit = 'Edit',
  Delete = 'Delete',
  Active = 'Active',
  Inactive = 'Inactive',
}

const PREFIX = 'TransportationAttributesGridContainer';

const classes = {
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
};

const ActionCellBox = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'end',
  width: '100%',
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
    opacity: 0,
    '.MuiDataGrid-row:hover &': {
      opacity: 1,
    },
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '170px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body2,
}));

export const TransportationAttributesGridContainer = ({
  rowItems,
  isLoading,
  handleMenuItemClick,
}: TransportationAttributesGridContainerProps) => {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] =
    useState<TransportationAttributeGridRow | null>(null);

  const transformToGridRows = (
    items: TransportationAttributeRowItem[],
    parentPath: string[] = [],
  ): TransportationAttributeGridRow[] => {
    const result: TransportationAttributeGridRow[] = [];

    items.forEach((item) => {
      const currentPath = [
        ...parentPath,
        `${item.name}#${item.transportationAttributeUUID}`,
      ];

      const gridRow: TransportationAttributeGridRow = {
        id: `${item.transportationAttributeUUID}#${parentPath}`,
        name: item.name,
        category: item.category,
        status: item.status,
        hierarchy: currentPath,
        transportationAttributeUUID: item.transportationAttributeUUID,
      };

      result.push(gridRow);

      if (item.children && item.children.length > 0) {
        const childRows = transformToGridRows(item.children, currentPath);
        result.push(...childRows);
      }
    });

    return result;
  };

  const rows: GridRowsProp<TransportationAttributeGridRow> =
    transformToGridRows(rowItems);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    transportationAttributeUUID: string,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    const matchingRow = rows.find(
      (row) => row.transportationAttributeUUID === transportationAttributeUUID,
    );
    if (matchingRow) {
      setSelectedRow(matchingRow);
    }
  };

  const handleEditClick = () => {
    setMenuAnchorEl(null);
    if (selectedRow) {
      handleMenuItemClick(
        TransportationAttributeMenuItem.Edit,
        selectedRow.transportationAttributeUUID,
      );
    }
  };

  const handleSetActiveClick = () => {
    setMenuAnchorEl(null);
    if (selectedRow) {
      handleMenuItemClick(
        TransportationAttributeMenuItem.Active,
        selectedRow.transportationAttributeUUID,
      );
    }
  };

  const handleSetInactiveClick = () => {
    setMenuAnchorEl(null);
    if (selectedRow) {
      handleMenuItemClick(
        TransportationAttributeMenuItem.Inactive,
        selectedRow.transportationAttributeUUID,
      );
    }
  };

  const handleDeleteClick = () => {
    setMenuAnchorEl(null);
    if (selectedRow) {
      handleMenuItemClick(
        TransportationAttributeMenuItem.Delete,
        selectedRow.transportationAttributeUUID,
      );
    }
  };

  const handleRowClick = (uuid: string) => {
    handleMenuItemClick(TransportationAttributeMenuItem.Edit, uuid);
  };

  const columns: GridColDef[] = [
    {
      field: 'category',
      headerName: t('transportationAttributes.category'),
      minWidth: 300,
      cellClassName: 'categoryCell',
    },
    {
      field: 'status',
      headerName: t('transportationAttributes.status'),
      headerClassName: 'statusHeader',
      sortable: false,
      minWidth: 87,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%">
          <CommonEntityStateChip state={params.row.status} />
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        if (isSuperReaderUser) {
          return null;
        }
        return (
          <ActionCellBox>
            <IconButton
              className={classes.moreButton}
              onClick={(event) =>
                handleMenuClick(event, params.row.transportationAttributeUUID)
              }>
              <MoreVert />
            </IconButton>
          </ActionCellBox>
        );
      },
    },
  ];

  return (
    <>
      <TransportationAttributesGrid
        rows={rows}
        columns={columns}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 120,
        }}>
        <StyledMenuItem onClick={() => handleEditClick()}>
          {t('transportationAttributes.edit')}
        </StyledMenuItem>
        {(selectedRow?.status === CommonEntityState.Draft ||
          selectedRow?.status === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleSetActiveClick()}>
            {t('transportationAttributes.setActive')}
          </StyledMenuItem>
        )}
        {selectedRow?.status === CommonEntityState.Active && (
          <StyledMenuItem onClick={() => handleSetInactiveClick()}>
            {t('transportationAttributes.setInactive')}
          </StyledMenuItem>
        )}
        {(selectedRow?.status === CommonEntityState.Draft ||
          selectedRow?.status === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleDeleteClick()}>
            {t('transportationAttributes.delete')}
          </StyledMenuItem>
        )}
      </StyledMenu>
    </>
  );
};
